import { useParams, Link } from "react-router-dom";
import { getArticleBySlug, getLatestArticles } from "@/mocks/articles";
import { getCategoryBySlug } from "@/mocks/categories";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || "");
  const relatedArticles = getLatestArticles(3).filter((a) => a.slug !== slug);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = getCategoryBySlug(article.category);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      "@type": "Organization",
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    publisher: {
      "@type": "Organization",
      name: "糖果直播",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://tangguolive.com/article/${article.slug}`,
    },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: { "@id": "https://tangguolive.com", name: "首页" },
      },
      {
        "@type": "ListItem",
        position: 2,
        item: {
          "@id": `https://tangguolive.com/category/${article.category}`,
          name: category?.name || "分类",
        },
      },
      {
        "@type": "ListItem",
        position: 3,
        item: {
          "@id": `https://tangguolive.com/article/${article.slug}`,
          name: article.title,
        },
      },
    ],
  };

  return (
    <>
      <title>{`${article.title} - 糖果直播`}</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={`糖果直播,${article.tags.join(",")},直播`} />
      <link rel="canonical" href={`https://tangguolive.com/article/${article.slug}`} />

      <meta property="og:type" content="article" />
      <meta property="og:title" content={`${article.title} - 糖果直播`} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:url" content={`https://tangguolive.com/article/${article.slug}`} />
      <meta property="og:image" content={article.image} />
      <meta property="article:published_time" content={article.date} />
      <meta property="article:author" content={article.author} />
      {article.tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.title} />
      <meta name="twitter:description" content={article.excerpt} />
      <meta name="twitter:image" content={article.image} />

      <script type="application/ld+json">
        {JSON.stringify(articleJsonLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbLd)}
      </script>

      <article className="pt-20 pb-14 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                {
                  label: category?.name || "分类",
                  href: `/category/${article.category}`,
                },
                { label: article.title },
              ]}
            />
          </div>

          <header className="mb-8">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 whitespace-nowrap">
              {category?.name || "资讯"}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span>{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <div className="flex items-center gap-2 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 text-xs bg-background-100 text-foreground-500 rounded whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </header>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`糖果直播-${article.title}`}
              className="w-full h-auto object-cover max-h-[400px]"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none
              prose-headings:text-foreground-950 prose-headings:font-bold
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
              prose-li:text-foreground-700 prose-li:leading-relaxed
              prose-strong:text-foreground-900
              prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3
              prose-ul:mb-6"
            style={{ fontFamily: "var(--font-body)" }}
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <footer className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <Link
                to={`/category/${article.category}`}
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回{category?.name || "列表"}
              </Link>
            </div>
          </footer>

          {relatedArticles.length > 0 && (
            <section className="mt-14 pt-10 border-t border-background-200/70">
              <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => {
                  const relCat = getCategoryBySlug(rel.category);
                  return (
                    <Link
                      key={rel.slug}
                      to={`/article/${rel.slug}`}
                      className="group rounded-xl bg-background-100 overflow-hidden hover:bg-background-200/30 transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-full h-40 overflow-hidden">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <span className="inline-block px-2 py-0.5 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-2 whitespace-nowrap">
                          {relCat?.name || "资讯"}
                        </span>
                        <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                          {rel.title}
                        </h3>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}